import { Badge, cn } from '@paguz/ui';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  BotIcon,
  ClipboardCheckIcon,
  GaugeIcon,
  LandmarkIcon,
  PercentIcon,
  ShieldAlertIcon,
  Undo2Icon,
  WalletIcon,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { PageHeader } from '../../components/page.tsx';
import { useCan } from '../../lib/session.ts';
import { DecisionPolicySection, DelinquencySection, ReversalSection } from './policy-sections.tsx';
import { FundingSection, ProductSection } from './product-sections.tsx';
import { AiSection, LimitsSection, OriginationSection } from './p1-sections.tsx';

export const CONFIG_SECTIONS = [
  {
    slug: 'decision',
    label: 'Política de decisión',
    hint: 'Autoaprobación',
    icon: ClipboardCheckIcon,
    component: DecisionPolicySection,
    editable: true,
  },
  {
    slug: 'reversion',
    label: 'Reversión y disputas',
    hint: 'Qué se devuelve',
    icon: Undo2Icon,
    component: ReversalSection,
    editable: true,
  },
  {
    slug: 'mora',
    label: 'Política de mora',
    hint: 'Bloqueo y calificación',
    icon: ShieldAlertIcon,
    component: DelinquencySection,
    editable: true,
  },
  {
    slug: 'fondeo',
    label: 'Fondeo y GMF',
    hint: 'Cuenta puente o JIT',
    icon: WalletIcon,
    component: FundingSection,
    editable: true,
  },
  {
    slug: 'producto',
    label: 'Producto y tasas',
    hint: 'Usura, cuotas, cupos',
    icon: PercentIcon,
    component: ProductSection,
    editable: true,
  },
  {
    slug: 'limites',
    label: 'Límites transaccionales',
    hint: 'Reglas M8',
    icon: GaugeIcon,
    component: LimitsSection,
    editable: true,
  },
  {
    slug: 'originacion',
    label: 'Originación',
    hint: 'Scorecard y documentos',
    icon: LandmarkIcon,
    component: OriginationSection,
    editable: true,
  },
  {
    slug: 'ia',
    label: 'IA y voz',
    hint: 'Modelos y presupuestos',
    icon: BotIcon,
    component: AiSection,
    editable: true,
  },
] as const satisfies readonly {
  slug: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  component: ComponentType;
  editable: boolean;
}[];

export type ConfigSectionSlug = (typeof CONFIG_SECTIONS)[number]['slug'];

const route = getRouteApi('/app/configuracion/$section');

export function ConfigPage() {
  const { section } = route.useParams();
  const canEdit = useCan('config:edit');
  const current = CONFIG_SECTIONS.find((s) => s.slug === section) ?? CONFIG_SECTIONS[0];
  const Section = current.component;

  return (
    <>
      <PageHeader
        title="Configuración"
        description="Producto de crédito y políticas del tenant. Cada cambio crea una versión con vigencia, pide doble confirmación con motivo y queda en auditoría."
        actions={!canEdit && <Badge variant="outline">Solo lectura para tu rol</Badge>}
      />
      <div className="grid gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <nav aria-label="Secciones de configuración" className="lg:sticky lg:top-24 lg:self-start">
          <ul className="flex gap-1 overflow-x-auto lg:flex-col">
            {CONFIG_SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = s.slug === current.slug;
              return (
                <li key={s.slug} className="shrink-0">
                  <Link
                    to="/configuracion/$section"
                    params={{ section: s.slug }}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-accent',
                      active &&
                        'bg-highlight-soft text-highlight-soft-foreground hover:bg-highlight-soft',
                    )}
                  >
                    <Icon
                      className={cn(
                        'mt-0.5 size-[1.125rem] shrink-0',
                        active ? 'text-highlight' : 'text-muted-foreground',
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{s.label}</span>
                      <span className="block text-xs text-muted-foreground">{s.hint}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="min-w-0">
          <Section key={current.slug} />
        </div>
      </div>
    </>
  );
}
